import React from "react";
import { motion } from "framer-motion";
import {
  Trophy,
  Users,
  BookOpen,
  Mic,
  Palette,
  HeartHandshake,
  CalendarDays,
  ArrowRight,
} from "lucide-react";
const ActivitiesSection = () => {
  const activities = [
    {
      icon: <BookOpen size={26} />,
      title: "Academic Activities",
      desc: "Seminars, workshops, lectures, and academic programs for better learning.",
    },
    {
      icon: <Trophy size={26} />,
      title: "Sports Activities",
      desc: "Indoor and outdoor games to support physical fitness and teamwork.",
    },
    {
      icon: <Mic size={26} />,
      title: "Cultural Programs",
      desc: "Cultural events, debates, speeches, and stage activities for confidence.",
    },
    {
      icon: <Palette size={26} />,
      title: "Creative Activities",
      desc: "Art, poster making, writing, and creative participation for students.",
    },
    {
      icon: <Users size={26} />,
      title: "Student Development",
      desc: "Personality development, leadership, communication, and skill-building.",
    },
    {
      icon: <HeartHandshake size={26} />,
      title: "Social Awareness",
      desc: "Programs focused on social responsibility, awareness, and values.",
    },
  ];
  return (
    <section className="w-full bg-[#F6F8FC] py-12 md:py-14 overflow-hidden">
      <div className="w-full px-6 lg:px-12">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 25 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center max-w-3xl mx-auto mb-10"
          >
            <span className="inline-flex items-center gap-2 bg-[#071A52] text-[#FFD700] px-5 py-2 rounded-full text-xs font-black uppercase tracking-wide mb-4 shadow">
              <CalendarDays size={15} />
              College Activities
            </span>
            <h2 className="text-3xl md:text-4xl font-black text-[#071A52] leading-tight">
              Activities & Student Development
            </h2>
            <div className="w-24 h-1.5 bg-[#FFD700] rounded-full mx-auto mt-4 mb-4"></div>
            <p className="text-gray-600 text-sm md:text-base leading-relaxed">
              Our college encourages students to participate in academic,
              cultural, sports, and social activities for overall development.
            </p>
          </motion.div>
          <div className="grid lg:grid-cols-12 gap-8 items-stretch">
            <motion.div
              initial={{ opacity: 0, x: -35 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.65 }}
              viewport={{ once: true }}
              className="lg:col-span-4"
            >
              <div className="h-full relative overflow-hidden rounded-[30px] bg-gradient-to-br from-[#071A52] via-[#0B1F5C] to-[#102B75] p-7 md:p-8 shadow-xl text-white">
                <div className="absolute -top-14 -right-14 w-48 h-48 bg-[#FFD700]/20 rounded-full blur-3xl"></div>
                <div className="absolute -bottom-16 -left-16 w-56 h-56 bg-white/10 rounded-full blur-3xl"></div>

                <div className="relative z-10">
                  <div className="w-16 h-16 rounded-2xl bg-[#FFD700] text-[#071A52] flex items-center justify-center mb-6">
                    <Trophy size={32} />
                  </div>
                  <h3 className="text-2xl md:text-3xl font-black leading-tight mb-4">
                    Learning Beyond Classrooms
                  </h3>
                  <p className="text-white/75 text-sm leading-relaxed mb-6">
                    We believe education is not limited to books. Activities
                    help students improve confidence, leadership, discipline,
                    creativity, and teamwork.
                  </p>
                  <div className="space-y-3">
                    {[
                      "Academic Growth",
                      "Cultural Participation",
                      "Sports & Fitness",
                      "Personality Development",
                    ].map((item, index) => (
                      <div
                        key={index}
                        className="flex items-center gap-3 bg-white/10 border border-white/15 rounded-xl px-4 py-3"
                      >
                        <ArrowRight size={16} className="text-[#FFD700]" />
                        <span className="text-sm font-semibold">{item}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </motion.div>
            <div className="lg:col-span-8 grid sm:grid-cols-2 gap-5">
              {activities.map((item, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: index * 0.08 }}
                  viewport={{ once: true }}
                  className="group bg-white rounded-[24px] p-5 md:p-6 border border-gray-100 shadow-md hover:shadow-2xl hover:-translate-y-1 transition-all duration-300"
                >
                  <div className="w-14 h-14 rounded-2xl bg-[#071A52] text-[#FFD700] flex items-center justify-center mb-5 group-hover:bg-[#FFD700] group-hover:text-[#071A52] transition-all">
                    {item.icon}
                  </div>

                  <h3 className="text-lg font-black text-[#071A52] mb-2">
                    {item.title}
                  </h3>

                  <p className="text-gray-600 text-sm leading-relaxed">
                    {item.desc}
                  </p>
                </motion.div>
              ))}
            </div>
          </div>
          <motion.div
            initial={{ opacity: 0, y: 25 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="mt-8 bg-white rounded-[26px] p-5 md:p-6 shadow-md border border-gray-100 flex flex-col md:flex-row md:items-center md:justify-between gap-4"
          >
            <div>
              <h3 className="text-xl md:text-2xl font-black text-[#071A52]">
                Encouraging Every Student to Participate
              </h3>
              <p className="text-gray-600 text-sm mt-1">
                Activities help students grow with confidence, discipline, and
                responsibility.
              </p>
            </div>
            <a
              href="/contact"
              className="inline-flex items-center justify-center gap-2 bg-[#071A52] text-[#FFD700] px-6 py-3 rounded-xl text-sm font-black hover:bg-[#FFD700] hover:text-[#071A52] transition-all w-fit"
            >
              Contact College
              <ArrowRight size={16} />
            </a>
          </motion.div>
        </div>
      </div>
    </section>
  );
};
export default ActivitiesSection;